import { useEffect, useMemo, useState } from "react"
import * as Y from 'yjs'
import { WebsocketProvider } from 'y-websocket'
import { isArray, isObject } from "../utils"
import { slateNodesToInsertDelta } from "@slate-yjs/core"

const status = {}

const convertToY = (data, path = '') => {
  if (isArray(data)) {
    const yArray = new Y.Array()
    data.forEach(entry => yArray.push([convertToY(entry)]))
    return yArray
  } else if (isObject(data) && !isYMap(data) && !isYArray(data)) {
    const yMap = new Y.Map()
    Object.entries(data).forEach(([key, value]) => yMap.set(key, convertToY(value, path + ' ' + key)))
    return yMap
  } else {
    return data
  }
}

const convertToYText = (data) => {
  return isYText(data) ? data : new Y.Text(data.toString() ?? '')
}

const convertToYXmlText = (data) => {
  return isYXmlText(data) ? data : new Y.XmlText(data.toString() ?? '')
}

const convertKeyToY = (name, document) => {
  const data = document.doc.get(name)
  if (isYArray(data)) {
    data.toArray().forEach((entry, index) => {
      const newEntry = convertToY(entry)
      if (entry !== newEntry) {
        console.warn('Converting array to YArray')
        data.delete(index)
        data.insert(index, newEntry)
      }
    })
  } else if (isYMap(data)) {
    for (let entryKey of data.keys()) {
      const entry = data.get(entryKey)
      const newEntry = convertToY(entry)
      if (entry !== newEntry) {
        console.warn('Converting object to YMap')
        data.set(entryKey, newEntry)
      }
    }
  }
}

const isYMap = (element) => element instanceof Y.Map
const isYArray = (element) => element instanceof Y.Array
const isYText = (element) => element instanceof Y.Text
const isYXmlText = (element) => element instanceof Y.XmlText

const CODE_BLOCK_TYPE = 'code-block'
const CODE_BLOCK_LINE = 'code-line'

const toChildren = (content) => [{ text: content }]
const toCodeLines = (content) =>
  content
    .split('\n')
    .map(line => ({ type: CODE_BLOCK_LINE, children: toChildren(line) }))

const toSlateFormat = (code, language) => {
  return [
    {
      type: CODE_BLOCK_TYPE,
      language: language || 'css',
      children: toCodeLines(code || ''),
    }
  ]
}

const slateToString = (value) => {
  if (Array.isArray(value)) {
    return value.map(entry => {
      if (entry.text) return entry.text
      return slateToString(entry.children)
    }).join('')
  } else {
    return ''
  }
}

const getCurrentLine = (editor) => {
  const path = editor.selection?.anchor?.path
  if (!path) return ''
  return path.reduce((node, index) => (node.children && node.children[index]) || node, editor).text
}

const useSharedDocument = (id, options) => {
  const [document, setDocument] = useState()
  const [webSocket, setWebSocket] = useState()
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (!id) return
    setReady(false)
    const doc = new Y.Doc()
    let ws = null
    if (options.ws) {
      ws = new WebsocketProvider(options.ws, id, doc)
      ws.on('status', event => {
        status[id] = event.status
      })
      ws.on('sync', () => {
        const settings = doc.get('settings')
        convertKeyToY('data', settings)
        const theme = doc.get("theme.draft", Y.XmlText)
        if (theme._length === 0) {
          const themeOld = doc.getText("theme").toString()
          theme.applyDelta(slateNodesToInsertDelta(toSlateFormat(themeOld, 'css')))
        }
        const themeSellers = doc.get("themeSellers.draft", Y.XmlText)
        if (themeSellers._length === 0) {
          const themeSellersOld = doc.getText("themeSellers").toString()
          themeSellers.applyDelta(slateNodesToInsertDelta(toSlateFormat(themeSellersOld, 'css')))
        }
        setReady(true)
      })
    }
    setDocument(doc)
    setWebSocket(ws)
    return () => {
      if (options.ws) ws.destroy()
      doc.destroy()
      setWebSocket(null)
    }
  }, [id, options.ws])

  return [document, webSocket, ready]
}

const useDelayedYText = (object, delay = 1500) => {
  const [forceUpdate, setForceUpdate] = useState({})
  const [data, setData] = useState()

  useEffect(() => {
    const timer = setTimeout(() => setData(object.toString()), delay)
    return () => clearTimeout(timer)
  }, [object, delay, forceUpdate])

  useEffect(() => {
    if (!object) return null
    const observer = () => setForceUpdate({})
    if (typeof object.observe !== 'function') return
    object.observe(observer)
    return () => object.unobserve(observer)
  }, [object])

  return data
}

const useYObject = (object, deep = false) => {
  const [forceUpdate, setForceUpdate] = useState({})
  const data = useMemo(() => {
    if (!object) return null
    if (isYMap(object)) {
      const result = {}
      for (let key of object.keys()) {
        result[key] = object.get(key)
      }
      return result
    } else if (isYArray(object)) {
      return object ? object.toArray() : null
    } else {
      throw new Error('Not an Y object')
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [object, forceUpdate])

  useEffect(() => {
    if (!object) return null
    const observer = () => setForceUpdate({})
    if (typeof object.observe !== 'function') return
    if (deep) {
      object.observeDeep(observer)
      return () => object.unobserveDeep(observer)  
    }
    else {
      object.observe(observer)
      return () => object.unobserve(observer)
    }
  }, [object, deep])

  return data
}

export { 
  useSharedDocument, 
  useYObject, 
  useDelayedYText, 
  convertToY, 
  convertToYText, 
  convertToYXmlText, 
  isYArray, 
  isYMap, 
  isYText,
  toSlateFormat,
  slateToString,
  getCurrentLine,
  CODE_BLOCK_TYPE,
  CODE_BLOCK_LINE
}